import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map, of, switchMap, tap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';

import { SupplierService } from '../../services/supplier/supplier.service';
import { SupplierActions, SupplierApiActions } from './supplier.actions';

@Injectable()
export class SupplierEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _supplierService = inject(SupplierService);
  private readonly _toastService = inject(ToastService);

  public loadAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadAllocations),
      switchMap(({ surveyId, params }) =>
        this._supplierService.allocations(surveyId, params).pipe(
          map((allocations) =>
            SupplierApiActions.loadAllocationsSuccess({ allocations }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadAllocationsFailure({
                  error: err.message ?? 'Failed to load supplier allocations',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadBlends$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadBlends),
      switchMap(({ locale }) =>
        this._supplierService.blends(locale).pipe(
          map((blends) => SupplierApiActions.loadBlendsSuccess({ blends })),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadBlendsFailure({
                  error: err.message ?? 'Failed to load supplier blends',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadGroupTemplates$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadGroupTemplates),
      switchMap(() =>
        this._supplierService.groupTemplates().pipe(
          map((groupTemplates) =>
            SupplierApiActions.loadGroupTemplatesSuccess({ groupTemplates }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadGroupTemplatesFailure({
                  error:
                    err.message ?? 'Failed to load supplier group templates',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadFlexibilityPriceParams$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadFlexibilityPriceParams),
      switchMap(({ surveyId }) =>
        this._supplierService.flexibilityPriceParams(surveyId).pipe(
          map((params) =>
            SupplierApiActions.loadFlexibilityPriceParamsSuccess({ params }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadFlexibilityPriceParamsFailure({
                  error:
                    err.message ?? 'Failed to load flexibility price params',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // concatMap, not switchMap: an in-flight save must never be cancelled by a
  // second Apply — the user would be told it succeeded while it was dropped.
  public updateAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.updateAllocations),
      concatMap(({ surveyId, update }) =>
        this._supplierService.updateAllocations(surveyId, update).pipe(
          map((allocations) =>
            SupplierApiActions.updateAllocationsSuccess({ allocations }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.updateAllocationsFailure({
                  error: err.message ?? 'Failed to update suppliers',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // Same serialized PUT as updateAllocations$, but carrying the panel's own
  // per-row quantities + grouping instead of an even goal split.
  public updateManagedAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.updateManagedAllocations),
      concatMap(({ surveyId, update }) =>
        this._supplierService.updateManagedAllocations(surveyId, update).pipe(
          map((allocations) =>
            SupplierApiActions.updateAllocationsSuccess({
              allocations,
              blendId: update.context.blendId,
              priceOverride: update.context.globalOverrideValue.match({
                some: (value) => value,
                none: () => null,
              }),
              manualOverride: update.context.manualOverrideEnabled,
              surveyPauseThreshold: update.context.pauseThresholdValue.match({
                some: (value) => value,
                none: () => null,
              }),
              defaultPurePriceOverride: update.context.defaultPurePriceOverride,
              isPurePriceRateCardEnable:
                update.context.isPurePriceRateCardEnable,
              supplierDefaultAllocation:
                update.context.supplierDefaultAllocation,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.updateAllocationsFailure({
                  error: err.message ?? 'Failed to update suppliers',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // concatMap: serialize saves so a second click never cancels an in-flight one.
  public saveGroupAsTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.saveGroupAsTemplate),
      concatMap(({ data }) =>
        this._supplierService.saveGroupTemplate(data).pipe(
          tap(() =>
            this._toastService.success(
              'Supplier group template saved successfully.',
              { keepOpen: false },
            ),
          ),
          map(() => SupplierApiActions.saveGroupAsTemplateSuccess()),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.saveGroupAsTemplateFailure({
                  error:
                    err.message ?? 'Failed to save supplier group template',
                }),
              ),
            {
              notify: true,
              customMessage:
                'Unable to save this group template. Please refresh and try again.',
            },
          ),
        ),
      ),
    ),
  );

  // concatMap: serialize saves so a second click never cancels an in-flight one.
  public saveBlend$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.saveBlend),
      concatMap(({ data }) =>
        this._supplierService.saveBlend(data).pipe(
          tap(() =>
            this._toastService.success('Supplier blend saved successfully.', {
              keepOpen: false,
            }),
          ),
          map((blend) => SupplierApiActions.saveBlendSuccess({ blend })),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.saveBlendFailure({
                  error: err.message ?? 'Failed to save supplier blend',
                }),
              ),
            {
              notify: true,
              customMessage:
                'Unable to save this blend. Please refresh and try again.',
            },
          ),
        ),
      ),
    ),
  );

  // The saved blend carries its locale, so the list refetch reuses it — the new
  // blend then appears in the select alongside the rest.
  public reloadBlendsAfterSave$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.saveBlendSuccess),
      map(({ blend }) => SupplierActions.loadBlends({ locale: blend.locale })),
    ),
  );

  public loadRateCards$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadRateCards),
      switchMap(({ params }) =>
        this._supplierService.rateCards(params).pipe(
          map((rateCards) =>
            SupplierApiActions.loadRateCardsSuccess({ rateCards }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadRateCardsFailure({
                  error: err.message ?? 'Failed to load rate cards',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );
}
